/*
  Shopify Boilerplate - Main JS Entry Point
  ES Modules + Web Components approach
*/

// Import core web components
import './lazy-image.js';
import './reveal-image.js';
import './quantity-input.js';
import './cart-drawer.js';
import './collection-filters-2.js';
import './collection-slider.js';
import './product-form-2.js';
import './pdp.js';
import './product-card-form.js';
import './product-switcher.js';
import './collection-nav.js';
// protein-theme must come before tab-group: customElements.define() upgrades
// every <tab-group> already in the DOM synchronously, which fires the first
// tab-group:change before the next import line runs.
import './protein-theme.js';
import './tab-group.js';
import './scroll-slider.js';
import './product-recipe-nav-2.js';
import './product-recommendations.js';
import './announcement-bar.js';
import './mobile-nav.js';
import './header-nav.js';
import './predictive-search-2.js';
import './custom-select.js';
import './build-a-box.js';
import './login-toggle.js';
import './address-forms.js';
import './feeding-guide.js';

// Import utility libraries
import { cartAPI } from './cart-api.js';

// Make cart API available globally for inline scripts if needed
window.cartAPI = cartAPI;

// Global DOM ready functionality
document.addEventListener('DOMContentLoaded', () => {
  // Remove 'no-js' class for progressive enhancement
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  // Focus management for accessibility
  setupFocusManagement();

  // Setup global keyboard navigation
  setupKeyboardNavigation();

  // Entrance animations via IntersectionObserver
  setupRevealAnimations();
});

/**
 * Reveal animations — adds .is-revealed when elements enter viewport
 */
function setupRevealAnimations() {
  const targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
  if (!targets.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

  targets.forEach((el) => observer.observe(el));
}

/**
 * Focus management for accessibility
 */
function setupFocusManagement() {
  // Skip to content link
  const skipLink = document.querySelector('.skip-to-content-link');
  if (skipLink) {
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(skipLink.getAttribute('href'));
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth' });
      }
    });
  }
  
  // Focus trap for modals/drawers (handled by individual components)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      // Let components handle their own escape key behavior
      const event = new CustomEvent('escape-pressed');
      document.dispatchEvent(event);
    }
  });
}

/**
 * Keyboard navigation helpers
 */
function setupKeyboardNavigation() {
  // Arrow key navigation for product grids
  document.addEventListener('keydown', (e) => {
    if (e.target.matches('[data-product-grid] .product-card')) {
      const grid = e.target.closest('[data-product-grid]');
      const cards = Array.from(grid.querySelectorAll('.product-card'));
      const currentIndex = cards.indexOf(e.target);
      
      let nextIndex;
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = (currentIndex + 1) % cards.length;
          cards[nextIndex].focus();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = currentIndex === 0 ? cards.length - 1 : currentIndex - 1;
          cards[nextIndex].focus();
          break;
      }
    }
  });
}

// Shared utilities live in utils.js. Re-exported here so existing importers
// of './main.js' keep working, but component modules should import from
// './utils.js' directly to avoid loading a second copy of this entry point.
export { debounce, throttle, dispatchCustomEvent } from './utils.js';

// Console info for developers
console.info('🚀 Shopify Boilerplate loaded - Web Components + ES Modules');
console.info('📱 Mobile-first, vanilla JS, no build step required');